import Swal from "sweetalert2";
import moment from "moment-timezone";
/**
 * Validates the direction inputs based on the transfer type.
 *
 * @param {string} transferType - The type of the transfer (e.g., 'transfer', 'hourly', etc.).
 * @param {string} from - The starting location of the transfer.
 * @param {string} to - The destination location of the transfer.
 * @returns {boolean} - Returns true if the direction inputs are valid, false otherwise.
 */
export function isValidDirection(transferType, from, to) {
    if (transferType !== "hourly") {
        return to !== "" && from !== ""
    }
    return from !== "";
}

/**
 * Validates the date and time inputs for a reservation.
 *
 * @param {string} pickupDate - The date of pickup in 'YYYY-MM-DD' format.
 * @param {string} pickupTime - The time of pickup in 'HH:MM' format.
 * @returns {boolean} - Returns true if both date and time are provided, false otherwise.
 */
export function isValidDateAndTime(pickupDate, pickupTime) {
    return pickupDate !== "" && pickupTime !== "";
}

/**
 * Validates the passenger count for a reservation.
 *
 * @param {number} passengersCount - The number of passengers for the reservation.
 * @returns {boolean} - Returns true if the passenger count is valid, false otherwise.
 */
export function isValidPassengerCount(passengersCount) {
    return Number.isInteger(passengersCount) && passengersCount > 0;
}

/**
 * Validates the number of hours for an hourly transfer.
 *
 * @param {string} transferType - The type of the transfer (e.g., 'transfer', 'hourly', etc.).
 * @param {number} hours - The number of hours for the reservation.
 * @returns {boolean} - Returns true if the number of hours is valid for an hourly transfer, false otherwise.
 */
export function isValidNumerHours(transferType, quotedHours) {
    if (transferType === "hourly") {
        return Number.isInteger(quotedHours) && quotedHours > 0;
    }
    return true;
}

/**
 * Determines if a reservation can be requoted based on various parameters.
 *
 * @param {string} transferType - The type of the transfer (e.g., 'transfer', 'hourly', etc.).
 * @param {string} from - The starting location of the reservation.
 * @param {string} to - The destination location of the reservation.
 * @param {string} pickupDate - The date of pickup in 'YYYY-MM-DD' format.
 * @param {string} pickupTime - The time of pickup in 'HH:MM' format.
 * @param {number} passengersCount - The number of passengers for the reservation.
 * @returns {boolean} - Returns true if the reservation can be requoted, false otherwise.
 */
export function canBeRequoted(transferType, from, to, pickupDate, pickupTime, quotedHours, passengersCount) {

    if (!isValidDirection(transferType, from, to)) {
        return false;
    }

    if (!isValidDateAndTime(pickupDate, pickupTime)) {
        return false;
    }

    if (!isValidPassengerCount(passengersCount)) {
        return false;
    }

    if (!isValidNumerHours(transferType, quotedHours)) {
        return false;
    }

    return true;
}

/**
 * Formats a date to the "DD-MMM-YYYY" format for sold-out notifications.
 *
 * @param {string|Date} date - The date to format.
 * @returns {string} - The formatted date string.
 */
export function formatBlackoutDate(date) {
    return moment.parseZone(date).format("DD-MMM-YYYY");
}

/**
 * Gets the unit format string based on the provided unit code.
 *
 * @param {string} unit - The unit code (e.g., 'HOURS', 'DAYS', etc.).
 * @returns {string} - The corresponding unit format string.
 */
export function getUnitFormat(unit) {
    if (unit === "HOURS") {
        return "hours";
    } else if (unit === "DAYS") {
        return "days";
    } else {
        return "months";
    }
}

/**
 * Builds a UTC datetime string from the provided pickup date and time.
 *
 * @param {string} pickupDate - The date of pickup in 'YYYY-MM-DD' format.
 * @param {string} pickupTime - The time of pickup in 'HH:MM AM/PM' format.
 * @returns {string} - The combined UTC datetime string in ISO format.
 */
export function buildUtcPickupDateTime(pickupDate, pickupTime) {
    const parsedDate = moment(pickupDate);
    const parsedTime = moment(pickupTime, "h:mm A");
    const combined = parsedDate.set({
        hour: parsedTime.hour(),
        minute: parsedTime.minute(),
        second: parsedTime.second()
    });
    return combined.utc(true).format();
}

/**
 * Handles the confirmation of a blackout period for a service.
 *
 * @param {Object} service - The service object containing blackout information.
 * @returns {Promise<void>}
 */
export async function handleBlackoutConfirmation(service, callback = async function () { }) {
    const { start_date, end_date } = service.blackout;
    let { value: confirmed } = await Swal.fire({
        title: "BlackOut Date",
        html: `The selected service has a <br> blackout period in effect from <br><strong>${formatBlackoutDate(start_date)} </strong> to <strong>${formatBlackoutDate(end_date)}</strong>. </br></br> Are you sure do you want continue?`,
        imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
        buttonsStyling: false,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        reverseButtons: true,
        allowOutsideClick: false,
        cancelButtonText: "No",
        confirmButtonText: "Yes",
        confirmButtonClass: "btn btn-primary btn-small-one"
    });
    callback(confirmed, service);
}

/** * Handles the confirmation of an advanced booking policy for a service.
 *
 * @param {Object} service - The service object containing advanced booking policy information.
 * @returns {Promise<void>}
 */
export async function handleCutOffConfirmation(service, callback = async function () { }) {
    const { value, unit } = service.cutoffDuration;
    let { value: confirmed } = await Swal.fire({
        title: "Advanced booking policy!",
        html: `This Supplier has an advanced booking policy of </br><strong>${value} ${getUnitFormat(unit)}.</strong></br></br> Do you still want to update this reservation?`,
        imageUrl: require("rhomtrip_assets/src/img/icons/alerts/question.png"),
        buttonsStyling: false,
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        reverseButtons: true,
        cancelButtonText: "No",
        confirmButtonText: "Yes",
        confirmButtonClass: "btn btn-primary btn-small-one"
    });
    callback(confirmed, service);
}

/**
 * Returns true if the reservation status blocks re-quoting.
 * Cancelled ("CANCELLED"), completed ("JF"), and no-show ("NS") reservations
 * must not trigger a new quote when a requotable field is modified.
 *
 * @param {string} reservationStatus - The current status code of the reservation.
 * @returns {boolean}
 */
export function isRequoteBlockedByStatus(reservationStatus) {
    return ["SCB", "SBC", "JF", "NS"].includes(reservationStatus);
}

/**
 * Checks if the service has available seats for the given number of passengers.
 *
 * @param {Object} service - The service object containing vehicle information.
 * @param {number} passengersCount - The number of passengers for the reservation.
 * @returns {boolean} - Returns true if there are available seats, false otherwise.
 */
export function hasAvailableSeats(service, passengersCount) {
    if (!service) return false;

    const { vehicle } = service;

    if (!vehicle) return false;

    return passengersCount <= vehicle.seats;
}

/**
 * Checks whether a latitude/longitude pair is within valid geographic bounds.
 *
 * @param {number} lat - The latitude to validate.
 * @param {number} lng - The longitude to validate.
 * @returns {boolean} - Returns true if both coordinates are valid numbers within range.
 */
export function isValidCoordinates(lat, lng) {
    const isLatValid = typeof lat === "number" && lat >= -90 && lat <= 90;
    const isLngValid = typeof lng === "number" && lng >= -180 && lng <= 180;
    return isLatValid && isLngValid;
}

/**
 * Resolves the location sent to the product quotation.
 * Uses the structured point (e.g. airport) when it applies to the transfer type and has valid coordinates,
 * otherwise falls back to the plain direction.
 *
 * @param {boolean} isTransferType - Whether the point applies to the product transfer type (ARRIVAL origin / DEPARTURE destination).
 * @param {Object|null} point - The structured location data.
 * @param {string} direction - The plain direction text.
 * @returns {Object|string|null} - The structured point, the direction, or null.
 */
export function getPoint(isTransferType, point, direction) {
    if (isTransferType && point) {
        return isValidCoordinates(point.location.lat, point.location.lng)
            ? {
                select: point.select,
                location: point.location,
                owner: point.owner,
                country_code: point.country_code,
                place_id: point.place_id,
                type: point.type
            }
            : direction;
    } else {
        return direction ? direction : null;
    }
}
